import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "../../../lib/motion/index.js";
import { useCalendarsStore, useSettingsStore, useSheetMorphStore, useTasksStore, useThemeStore, useTopicsStore } from "../../../stores/index.js";
import { resolvedCalendarColor, resolvedTopicColors } from "../../entity-colors.js";
import { effectiveMatrixDisplayPos, systemTimeZone } from "@app/core";
import { matrixAccentFromPos } from "../../task-colors.js";
import { mobileBlockStyle, type MobileBlockStyle } from "../mobile-block-style.js";
import type { MorphRect } from "../../../stores/sheetMorph.store.js";
import { matrixEnabledFor } from "../../../stores/settings.store.js";

interface Anim {
  token: number;
  source: MorphRect;
  target: MorphRect;
  style: MobileBlockStyle;
  title: string;
}

/**
 * "Drop into place": after a save on a calendar route, a styled clone of the
 * sheet flies from the sheet's rect to the new block's slot and fades into the
 * real block — so the popup and the final element read as the same object.
 * Mounted once in Shell (mobile). Gracefully no-ops when the slot isn't on
 * screen (the sheet has already closed) or under reduced motion.
 */
export function SheetMorph() {
  const pending = useSheetMorphStore((s) => s.pending);
  const clear = useSheetMorphStore((s) => s.clear);
  const reduce = useReducedMotion();
  const [anim, setAnim] = useState<Anim | null>(null);

  useEffect(() => {
    if (!pending) return;
    if (reduce) { clear(); return; }
    const { token, taskId, source } = pending;
    const task = useTasksStore.getState().tasks.find((t) => t.id === taskId);
    if (!task) { clear(); return; }
    const topics = useTopicsStore.getState().topics;
    const calendars = useCalendarsStore.getState().calendars;
    const theme = useThemeStore.getState().config;
    const settings = useSettingsStore.getState();
    const topicColors = resolvedTopicColors(theme, topics, task.topic_ids);
    const calendarColor = resolvedCalendarColor(theme, calendars, task.calendar_id);
    // Imperative twin of `useMatrixPriorityColor` (this runs in an effect, not
    // render): continuous diagonal color only for a matrix-PLACED task in
    // scope — must match what the target block renders, or the morph would
    // land in the wrong border color.
    const matrixColor = matrixEnabledFor(settings.matrixScope, task.is_event) && task.matrix_pos
      ? matrixAccentFromPos(effectiveMatrixDisplayPos(
          task.matrix_pos, task.priority, task.deadline_at ?? task.due_at, new Date(),
          task.tz ?? settings.defaultTz ?? systemTimeZone(),
          { enabled: settings.deadlineUrgency, highWithinDays: settings.deadlineUrgencyHighDays, mediumWithinDays: settings.deadlineUrgencyMediumDays },
        ))
      : null;
    const style = mobileBlockStyle({
      priority: task.priority,
      matrixColor,
      customColor: task.custom_color,
      topicColors,
      calendarColor,
      isEvent: task.is_event,
    });

    let raf = 0;
    let tries = 0;
    const find = (): HTMLElement | null =>
      document.querySelector<HTMLElement>(`[data-task-id="${taskId}"]`) ??
      document.querySelector<HTMLElement>(`[data-task-id^="${taskId}::v::"]`);
    const tick = () => {
      const el = find();
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setAnim({ token, source, target: { left: r.left, top: r.top, width: r.width, height: r.height }, style, title: task.title });
          return;
        }
      }
      if (++tries > 36) { clear(); return; } // ~600ms then give up (plain close)
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending?.token]);

  if (!anim) return null;
  return createPortal(
    <motion.div
      aria-hidden
      className="pointer-events-none fixed z-[70] overflow-hidden rounded border bg-card px-1.5 py-0.5 shadow-lg"
      style={{ background: anim.style.container.background, borderColor: anim.style.container.borderColor, boxShadow: anim.style.container.boxShadow }}
      initial={{ left: anim.source.left, top: anim.source.top, width: anim.source.width, height: anim.source.height, borderRadius: 18, opacity: 1 }}
      animate={{ left: anim.target.left, top: anim.target.top, width: anim.target.width, height: anim.target.height, borderRadius: 6 }}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      onAnimationComplete={() => { setAnim(null); clear(); }}
    >
      <span className="block truncate text-xs font-medium" style={anim.style.titleText}>{anim.title}</span>
    </motion.div>,
    document.body,
  );
}
